import type { SettableOfferRequestReportStatus } from '@houzy/schemas/offer-request-management';
import type {
    PartnerCockpitOfferRequestDetail,
    PartnerCockpitOfferRequestsPage,
} from '@houzy/schemas/partner-cockpit/offer-requests';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { useTrackEvent, type OfferRequestList } from '../../../lib/trackingEvents';
import { updateOfferRequestStatus } from './offerRequestsApi';
import { offerRequestDetailQueryKey } from './useOfferRequestDetail';
import { offerRequestsSegmentKey } from './useOfferRequestsList';

type Variables = {
    uuid: string;
    status: SettableOfferRequestReportStatus;
};
type Context = { previous: [readonly unknown[], unknown][]; offerRequestId: string | undefined };

const LIST_KEYS = [offerRequestsSegmentKey('new'), offerRequestsSegmentKey('open')];

// Tracked only where the lead was opened first (the detail page): the status metrics measure what partners do after
// opening a request, and a change from the list has no opening to set it against.
export function useUpdateOfferRequestStatus(
    trackedList?: OfferRequestList,
): UseMutationResult<void, Error, Variables, Context> {
    const queryClient = useQueryClient();
    const track = useTrackEvent();
    return useMutation({
        mutationFn: ({ uuid, status }) => updateOfferRequestStatus(uuid, status),
        onMutate: async ({ uuid, status }) => {
            await Promise.all([
                ...LIST_KEYS.map((queryKey) => queryClient.cancelQueries({ queryKey })),
                queryClient.cancelQueries({ queryKey: offerRequestDetailQueryKey(uuid) }),
            ]);
            const previousDetail = queryClient.getQueryData<PartnerCockpitOfferRequestDetail>(
                offerRequestDetailQueryKey(uuid),
            );
            const previous: Context['previous'] = [
                ...LIST_KEYS.flatMap((queryKey) =>
                    queryClient.getQueriesData<PartnerCockpitOfferRequestsPage>({ queryKey }),
                ),
                [offerRequestDetailQueryKey(uuid), previousDetail],
            ];
            for (const queryKey of LIST_KEYS) {
                queryClient.setQueriesData<PartnerCockpitOfferRequestsPage>({ queryKey }, (page) =>
                    page
                        ? {
                              ...page,
                              items: page.items.map((item) => (item.uuid === uuid ? { ...item, status } : item)),
                          }
                        : page,
                );
            }
            queryClient.setQueryData<PartnerCockpitOfferRequestDetail>(offerRequestDetailQueryKey(uuid), (detail) =>
                detail ? { ...detail, status } : detail,
            );
            return { previous, offerRequestId: previousDetail?.referenceId };
        },
        onSuccess: (_data, _variables, context) => {
            if (trackedList && context.offerRequestId !== undefined) {
                track('Offer Request Status Changed', { offerRequestId: context.offerRequestId, list: trackedList });
            }
        },
        onError: (_error, _variables, context) => {
            context?.previous.forEach(([key, data]) => queryClient.setQueryData(key, data));
        },
        onSettled: (_data, _error, { uuid }) => {
            for (const queryKey of LIST_KEYS) {
                void queryClient.invalidateQueries({ queryKey });
            }
            void queryClient.invalidateQueries({ queryKey: offerRequestDetailQueryKey(uuid) });
        },
    });
}
